import { useCallback, useEffect, useRef, useState } from 'react'
import { Mail, Paperclip, Eye, RotateCw } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { showApiError } from '@/lib/utils/notifications'
import applicationStyles from '@/features/application/styles/applicationList.module.css'
import { PageShell } from '@/components/layout/PageShell'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { ReportDateRangePicker } from '@/features/reports/components/ReportDateRangePicker'
import { MailDetailModal } from '../components/MailDetailModal'
import { getAllTemplateIds, getMailLogs } from '../services/mailtrapService'
import type { MailLog, MailLogsParams } from '../types'
import {
  compactMailLogsParams,
  mailtrapParamsEqual,
} from '../utils/mailtrapApiHelpers'

const DEFAULT_PARAMS = compactMailLogsParams({ page: 1, perPage: 15 })
const SESSION_KEY = 'mailtrap_filter'

function loadSessionParams(): MailLogsParams {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY)
    if (saved) {
      const parsed = JSON.parse(saved) as MailLogsParams
      if (parsed && typeof parsed === 'object') {
        return compactMailLogsParams({
          ...DEFAULT_PARAMS,
          ...parsed,
        })
      }
    }
  } catch (e) {
    console.error('Failed to load mailtrap_filter session:', e)
  }
  return DEFAULT_PARAMS
}

function saveSessionParams(params: MailLogsParams) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(params))
  } catch (e) {
    console.error('Failed to save mailtrap_filter session:', e)
  }
}

function formatDateTime(value: string | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

function attachmentCount(attachments: MailLog['attachments']): number {
  if (typeof attachments === 'number') return attachments
  if (Array.isArray(attachments)) return attachments.length
  return 0
}

/** Mail logs API expects `fromDt` / `toDt` as DD-MM-YYYY (Angular `getFilterValue` + moment). */
function isoYyyyMmDdToDdMmYyyy(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return undefined
  return `${match[3]}-${match[2]}-${match[1]}`
}

export function MailtrapListPage() {
  const location = useLocation()
  const [logs, setLogs] = useState<MailLog[]>([])
  const [total, setTotal] = useState(0)
  const [params, setParams] = useState<MailLogsParams>(DEFAULT_PARAMS)
  const [searchInput, setSearchInput] = useState('')
  const [fromDt, setFromDt] = useState<string | null>(null)
  const [toDt, setToDt] = useState<string | null>(null)
  const [lrFormInput, setLrFormInput] = useState('')
  const [templateOptions, setTemplateOptions] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [detailMailId, setDetailMailId] = useState<number | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const mountedRef = useRef(false)
  const fromDtRef = useRef(fromDt)
  const toDtRef = useRef(toDt)
  fromDtRef.current = fromDt
  toDtRef.current = toDt

  const load = useCallback(async (next: MailLogsParams) => {
    setLoading(true)
    try {
      const response = await getMailLogs(next)
      setLogs(response.records ?? [])
      setTotal(response.total ?? 0)
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true

    const queryQ = new URLSearchParams(location.search).get('q')
    const session = loadSessionParams()
    const merged = compactMailLogsParams({
      ...session,
      to: queryQ || session.to,
    })

    if (queryQ) {
      setSearchInput(queryQ)
    } else if (merged.to) {
      setSearchInput(merged.to)
    }

    if (merged.lr_form) {
      setLrFormInput(merged.lr_form)
    }

    setParams(merged)
    saveSessionParams(merged)
    void load(merged)
    getAllTemplateIds().then(setTemplateOptions).catch(console.error)
  }, [location.search, load])

  /** Debounced text filters (Search + LR Form); date changes apply immediately in `handleDateRangeChange`. */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setParams((prev) => {
        const next = compactMailLogsParams({
          page: 1,
          perPage: prev.perPage ?? 15,
          to: searchInput.trim() || undefined,
          lr_form: lrFormInput.trim() || undefined,
          fromDt: isoYyyyMmDdToDdMmYyyy(fromDtRef.current),
          toDt: isoYyyyMmDdToDdMmYyyy(toDtRef.current),
        })
        if (mailtrapParamsEqual(prev, next)) return prev
        saveSessionParams(next)
        void load(next)
        return next
      })
    }, 400)
    return () => window.clearTimeout(timer)
    // Intentionally omit `fromDt` / `toDt` from deps — date updates load immediately in `handleDateRangeChange`.
  }, [searchInput, lrFormInput, load])

  const applyParams = useCallback(
    (next: MailLogsParams) => {
      const compact = compactMailLogsParams(next)
      setParams(compact)
      saveSessionParams(compact)
      void load(compact)
    },
    [load],
  )

  const handleDateRangeChange = (startDate: Date | null, endDate: Date | null) => {
    const f = startDate ? formatDateForAPI(startDate) : null
    const t = endDate ? formatDateForAPI(endDate) : null
    setFromDt(f && f !== '' ? f : null)
    setToDt(t && t !== '' ? t : null)
    setParams((prev) => {
      const next = compactMailLogsParams({
        ...prev,
        page: 1,
        fromDt: isoYyyyMmDdToDdMmYyyy(f && f !== '' ? f : null),
        toDt: isoYyyyMmDdToDdMmYyyy(t && t !== '' ? t : null),
        to: searchInput.trim() || undefined,
        lr_form: lrFormInput.trim() || undefined,
      })
      if (mailtrapParamsEqual(prev, next)) return prev
      saveSessionParams(next)
      void load(next)
      return next
    })
  }

  const resetFilter = () => {
    setSearchInput('')
    setFromDt(null)
    setToDt(null)
    setLrFormInput('')
    try {
      sessionStorage.removeItem(SESSION_KEY)
    } catch {
      // ignore
    }
    applyParams(DEFAULT_PARAMS)
  }

  const refresh = () => applyParams(params)

  const goToPage = (pageNo: number) =>
    applyParams(compactMailLogsParams({ ...params, page: pageNo }))

  const perPage = params.perPage ?? 15
  const currentPage = params.page ?? 1
  const lastPage = Math.max(1, Math.ceil(total / perPage))
  const activeFilterCount = [
    fromDt || toDt,
    lrFormInput.trim(),
  ].filter(Boolean).length

  return (
    <PageShell title="MailTrap" pageHeaderTestIdRoot="mailtrap-page-header">
      <ListSearchToolbar
        searchValue={searchInput}
        searchPlaceholder="Search Mail..."
        onSearchChange={setSearchInput}
        onOpenFilters={() => setFiltersOpen(true)}
        activeFilterCount={activeFilterCount}
        testIdPrefix="mailtrap"
        actions={
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 md:flex-none"
          >
            <RotateCw className="h-[18px] w-[18px]" aria-hidden />
            Refresh
          </button>
        }
      />

      <ListFilterDrawer
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onReset={() => {
          resetFilter()
          setFiltersOpen(false)
        }}
        onDone={() => setFiltersOpen(false)}
        testIdPrefix="mailtrap"
      >
        <div className="space-y-5">
          <div
            className="space-y-2"
            data-testid="mailtrap-filters-daterange-section"
          >
            <label
              className="text-xs font-semibold uppercase tracking-wide text-slate-500"
              data-testid="mailtrap-filters-daterange-label"
            >
              Daterange
            </label>
            <ReportDateRangePicker
              startDate={fromDt}
              endDate={toDt}
              onChange={handleDateRangeChange}
              placeholder="Select Date Range"
              triggerClassName="!min-h-[3rem] !rounded-xl !border-slate-200 !bg-slate-50 !text-sm focus:!border-[rgb(21_151_255)] focus:!ring-2 focus:!ring-[rgb(21_151_255)]/15"
            />
          </div>

          <label className="block space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              LR Form
            </span>
            {Object.keys(templateOptions).length > 0 ? (
              <select
                id="mailtrap-lr-form"
                name="lr_form"
                value={lrFormInput}
                onChange={(e) => setLrFormInput(e.target.value)}
                className="w-full min-h-[3rem] rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15"
              >
                <option value="">Select LR Form</option>
                {Object.entries(templateOptions).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="mailtrap-lr-form"
                name="lr_form"
                type="text"
                value={lrFormInput}
                onChange={(e) => setLrFormInput(e.target.value)}
                placeholder="Template / form id"
                className="w-full min-h-[3rem] rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15"
                autoComplete="off"
              />
            )}
          </label>
        </div>
      </ListFilterDrawer>

      <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-4">
        <div className="flex flex-col items-stretch justify-between gap-3 border-b border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:px-6 sm:py-4">
          <div className="text-left text-xs text-slate-500 sm:text-sm">
            Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
            <span className="font-bold text-slate-900">{lastPage}</span>
            <span className="mx-2 text-slate-300">|</span>
            Total <span className="font-bold text-slate-900">{total}</span> records
          </div>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2">
            <button
              type="button"
              onClick={() => goToPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 text-sm font-medium"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => goToPage(Math.min(lastPage, currentPage + 1))}
              disabled={currentPage >= lastPage}
              className="px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 text-sm font-medium"
            >
              Next
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className={applicationStyles.listingTable}>
            <thead>
              <tr>
                <th className="w-10"></th>
                <th className="w-[18%]">To / From</th>
                <th className="w-[45%]">Subject</th>
                <th className="w-12">Att.</th>
                <th>Date</th>
                <th className="w-16">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Loading...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    <p className="max-w-2xl mx-auto">
                      No Records Found. Please try a different Search or Filter from the drop-downs above.
                    </p>
                  </td>
                </tr>
              ) : (
                logs.map((mail) => {
                  const attCount = attachmentCount(mail.attachments)
                  return (
                    <tr key={mail.mail_id}>
                      <td className="w-10">
                        <Mail className="h-4 w-4 text-amber-500" />
                      </td>
                      <td className="text-slate-700">
                        {mail.recipient || mail.fromName || '—'}
                      </td>
                      <td className="font-medium text-slate-900">
                        {mail.subject || '—'}
                      </td>
                      <td className="text-slate-600">
                        {attCount > 0 ? (
                          <span className="inline-flex items-center gap-1">
                            <Paperclip className="h-3 w-3" /> {attCount}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="text-slate-600 whitespace-nowrap">
                        {formatDateTime(mail.created_at)}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => setDetailMailId(mail.mail_id)}
                          className="p-1.5 rounded text-[#1597ff] hover:bg-[#1597ff]/10"
                          aria-label="View"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {lastPage > 1 && (
          <div className="flex flex-col items-stretch justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:px-6 sm:py-4">
            <div className="text-left text-xs text-slate-500 sm:text-sm">
              Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
              <span className="font-bold text-slate-900">{lastPage}</span>
            </div>
            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => goToPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 text-sm font-medium"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => goToPage(Math.min(lastPage, currentPage + 1))}
                disabled={currentPage >= lastPage}
                className="px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 text-sm font-medium"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <MailDetailModal
        isOpen={detailMailId !== null}
        mailId={detailMailId}
        onClose={() => setDetailMailId(null)}
        onSelectMailId={(nextId) => setDetailMailId(nextId)}
      />
    </PageShell>
  )
}
